import { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { api } from '../../lib/api';
import { useToasts } from '../../lib/toast';
import { Qr, loginQrData } from '../../lib/Qr';
import type { StaffRow, PermissionSet, StaffRole } from '@taj/types';

const PERM_LABELS: [keyof PermissionSet, string][] = [
  ['caisse', 'Caisse'], ['cuisine', 'Cuisine'], ['backOffice', 'Back-office'],
  ['canCancel', 'Annuler'], ['canRefund', 'Rembourser'], ['canClose', 'Clôturer'],
];
const ROLES: StaffRole[] = ['ADMIN', 'RESPONSABLE', 'CAISSIER'];
const emptyPerms: PermissionSet = { caisse: false, cuisine: false, backOffice: false, canCancel: false, canRefund: false, canClose: false };
type Form = { id?: string; fullName: string; role: StaffRole; pin: string; password: string; perms: PermissionSet };

export function StaffManager({ onBack }: { onBack: () => void }) {
  const push = useToasts((s) => s.push);
  const [list, setList] = useState<StaffRow[]>([]);
  const [form, setForm] = useState<Form | null>(null);
  const [saving, setSaving] = useState(false);
  const [qrStaff, setQrStaff] = useState<StaffRow | null>(null);

  const printQr = async (s: StaffRow) => {
    if (!s.loginToken) return;
    const dataUrl = await QRCode.toDataURL(loginQrData(s.loginToken), { width: 320, margin: 2 });
    const w = window.open('', '_blank');
    if (!w) return;
    w.document.write(`<html><head><title>QR ${s.fullName}</title></head><body style="text-align:center;font-family:sans-serif;padding:32px">
      <div style="font-family:sans-serif;font-weight:900;letter-spacing:.1em;color:#7a1f2b;font-size:22px">TAJ FUSION</div>
      <h2 style="margin:6px 0">${s.fullName}</h2>
      <div style="font-weight:800;color:#c8941e">${s.role}</div>
      <img src="${dataUrl}" style="margin:18px auto;display:block"/>
      <div style="color:#555">Scannez ce QR à l'écran de connexion</div></body></html>`);
    w.document.close(); w.focus(); setTimeout(() => w.print(), 300);
  };

  const regen = async (s: StaffRow) => {
    if (!confirm(`Régénérer le QR de « ${s.fullName} » ? L'ancien QR ne fonctionnera plus.`)) return;
    try {
      const updated = await api.regenStaffQr(s.id);
      setList((l) => l.map((x) => (x.id === s.id ? updated : x)));
      setQrStaff(updated);
      push('🔄 Nouveau QR généré');
    } catch (e: any) { push(e.message, 'warn'); }
  };

  const load = () => api.listStaff().then(setList).catch((e) => push(e.message, 'warn'));
  useEffect(load, []); // eslint-disable-line react-hooks/exhaustive-deps

  const startNew = () => setForm({ fullName: '', role: 'CAISSIER', pin: '', password: '', perms: { ...emptyPerms, caisse: true } });
  const startEdit = (s: StaffRow) => setForm({ id: s.id, fullName: s.fullName, role: s.role, pin: '', password: '', perms: { ...s.permissions } });

  const save = async () => {
    if (!form || !form.fullName.trim()) { push('Nom requis', 'warn'); return; }
    setSaving(true);
    try {
      const payload: any = { fullName: form.fullName.trim(), role: form.role, permissions: form.perms };
      if (form.pin.trim()) payload.pin = form.pin.trim();
      if (form.password.trim()) payload.password = form.password.trim();
      if (form.id) await api.updateStaff(form.id, payload);
      else await api.createStaff(payload);
      push('✅ Compte enregistré');
      setForm(null);
      load();
    } catch (e: any) { push(e.message, 'warn'); }
    finally { setSaving(false); }
  };

  const toggleActive = (s: StaffRow) => {
    setList((l) => l.map((x) => (x.id === s.id ? { ...x, active: !x.active } : x)));
    api.updateStaff(s.id, { active: !s.active }).catch((e) => push(e.message, 'warn'));
  };
  const remove = async (s: StaffRow) => {
    if (!confirm(`Supprimer le compte « ${s.fullName} » ?`)) return;
    try { await api.deleteStaff(s.id); setList((l) => l.filter((x) => x.id !== s.id)); } catch (e: any) { push(e.message, 'warn'); }
  };

  const setPerm = (k: keyof PermissionSet, v: boolean) => setForm((f) => (f ? { ...f, perms: { ...f.perms, [k]: v } } : f));

  return (
    <div className="staff-scroll">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '4px 0 14px', flexWrap: 'wrap' }}>
        <button className="km-back" onClick={onBack}>←</button>
        <div className="staff-title" style={{ fontSize: '1.4rem', flex: 1 }}>👥 Comptes & droits</div>
        {!form && <button className="btn-gold" style={{ width: 'auto', padding: '10px 16px' }} onClick={startNew}>＋ Nouveau compte</button>}
      </div>

      {/* Éditeur */}
      {form && (
        <div className="cc" style={{ maxWidth: 620, marginBottom: 16 }}>
          <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: '1.2rem', marginBottom: 10 }}>{form.id ? '✏️ Modifier le compte' : '➕ Nouveau compte'}</div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
            <input className="inp" style={{ flex: '2 1 180px' }} placeholder="Nom complet" value={form.fullName} onChange={(e) => setForm({ ...form, fullName: e.target.value })} />
            <select className="inp" style={{ width: 150 }} value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value as StaffRole })}>
              {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
            <input className="inp" style={{ width: 180 }} inputMode="numeric" placeholder={form.id ? 'Nouveau PIN (vide = inchangé)' : 'Code PIN (caissier)'} value={form.pin} onChange={(e) => setForm({ ...form, pin: e.target.value })} />
            <input className="inp" style={{ width: 220 }} type="password" placeholder={form.id ? 'Nouveau mot de passe (admin)' : 'Mot de passe (admin/respo)'} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
          </div>
          <div style={{ fontSize: '.72rem', fontWeight: 800, color: 'var(--ink-dim)', textTransform: 'uppercase', letterSpacing: '.06em', margin: '6px 0 6px' }}>Permissions</div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
            {PERM_LABELS.map(([k, lbl]) => (
              <label key={k} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: '.85rem', fontWeight: 700, color: 'var(--ink-dim)', cursor: 'pointer' }}>
                <input type="checkbox" checked={form.perms[k]} onChange={(e) => setPerm(k, e.target.checked)} /> {lbl}
              </label>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn-gold" style={{ width: 'auto', padding: '10px 20px' }} disabled={saving} onClick={save}>💾 Enregistrer</button>
            <button className="cf-reprint" style={{ width: 'auto', padding: '10px 16px' }} onClick={() => setForm(null)}>Annuler</button>
          </div>
        </div>
      )}

      {/* Liste */}
      <div className="cc" style={{ maxWidth: 720 }}>
        {list.map((s) => (
          <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: '1px solid var(--line)', flexWrap: 'wrap', opacity: s.active ? 1 : 0.5 }}>
            <div style={{ flex: '1 1 180px', minWidth: 150 }}>
              <div style={{ fontWeight: 800 }}>{s.fullName} <span style={{ fontSize: '.7rem', fontWeight: 800, color: 'var(--admin)', background: 'var(--admin-bg)', padding: '2px 8px', borderRadius: 20 }}>{s.role}</span></div>
              <div style={{ fontSize: '.74rem', color: 'var(--ink-faint)' }}>
                {s.hasPin ? '🔢 PIN' : ''} {s.hasPassword ? '🔑 mot de passe' : ''} · {PERM_LABELS.filter(([k]) => s.permissions[k]).map(([, l]) => l).join(', ') || 'aucun droit'}
              </div>
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '.76rem', fontWeight: 700, color: 'var(--ink-dim)' }}>
              <input type="checkbox" checked={s.active} onChange={() => toggleActive(s)} /> Actif
            </label>
            <button className="cr-btn" title="QR de connexion" onClick={() => setQrStaff(s)}>📱</button>
            <button className="cr-btn" title="Modifier" onClick={() => startEdit(s)}>✏️</button>
            <button className="cr-btn del" title="Supprimer" onClick={() => remove(s)}>🗑</button>
          </div>
        ))}
      </div>

      {/* Fenêtre QR de connexion */}
      {qrStaff && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', display: 'grid', placeItems: 'center', zIndex: 100, padding: 16 }} onClick={() => setQrStaff(null)}>
          <div className="cc" style={{ maxWidth: 360, textAlign: 'center', background: 'var(--paper)' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ fontWeight: 900, fontSize: '1.1rem', marginBottom: 2 }}>📱 QR de connexion</div>
            <div style={{ color: 'var(--ink-dim)', marginBottom: 12 }}>{qrStaff.fullName} · {qrStaff.role}</div>
            {qrStaff.loginToken
              ? <div style={{ display: 'grid', placeItems: 'center', padding: 10, background: '#fff', borderRadius: 12, width: 'fit-content', margin: '0 auto' }}><Qr data={loginQrData(qrStaff.loginToken)} size={210} /></div>
              : <div className="no-data">Génération…</div>}
            <div style={{ fontSize: '.8rem', color: 'var(--ink-dim)', margin: '12px 0' }}>
              À l'écran de connexion, ce compte scanne ce QR (douchette ou 📷 caméra) pour ouvrir sa session, sans taper de code.
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
              <button className="btn-gold" style={{ width: 'auto', padding: '9px 16px' }} onClick={() => printQr(qrStaff)}>🖨️ Imprimer</button>
              <button className="cf-reprint" style={{ width: 'auto', padding: '9px 16px' }} onClick={() => regen(qrStaff)}>🔄 Régénérer</button>
              <button className="cf-reprint" style={{ width: 'auto', padding: '9px 16px' }} onClick={() => setQrStaff(null)}>Fermer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
